import { Container, Graphics, Text, TextStyle, Texture, Rectangle, Sprite, Assets, BitmapText } from 'pixi.js';
import { shallow } from 'zustand/shallow';
import { useBattleStore } from '@/store/useBattleStore';
import { ControlState, type Skill } from '@/core/types/battle.types';
import { SkillManager } from '@/core/battle/SkillManager';
import { TurnManager } from '@/core/battle/TurnManager';
import { ControlStateMachine } from '@/core/battle/ControlStateMachine';
import skills from '@assets/skills/skills.png'

interface SkillBarConfig {
    iconSize: number;
    gap: number;
    borderWidth: number;
    direction: 'horizontal' | 'vertical';
    colors: {
        borderDefault: number;
        borderSelected: number;
        borderDisabled: number;
        background: number;
        text: number;
    };
    assets: {
        atlasUrl: string;
        iconSizeInAtlas: number;
    };
}

const DEFAULT_CONFIG: SkillBarConfig = {
    iconSize: 56,
    gap: 12,
    borderWidth: 3,
    direction: 'horizontal',
    colors: {
        borderDefault: 0x7a5b24,
        borderSelected: 0xd4a63a,
        borderDisabled: 0x2a1f16,
        background: 0x1b1712,
        text: 0xd8c7a0,
    },
    assets: {
        atlasUrl: skills,
        iconSizeInAtlas: 96,
    }
};

const PIP_SIZE = 12;
const PIP_GAP = 4;

interface SkillIcon {
    container: Container;
    background: Graphics;
    sprite: Sprite | Graphics;
    border: Graphics;
    skillId: string;
}

export class SkillBarRenderer extends Container {
    private config: SkillBarConfig;
    private icons: Map<string, SkillIcon> = new Map();
    private unsubscribe: (() => void) | null = null;
    private skillAtlasTexture: Texture | null = null;
    private pipActiveTexture: Texture | null = null;
    private pipDisabledTexture: Texture | null = null;
    private layoutHeight = 0;

    constructor(
        private skillManager: SkillManager,
        private turnManager: TurnManager,
        private stateMachine: ControlStateMachine,
        config: Partial<SkillBarConfig> = {}
    ) {
        super();
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.loadAssets();
        this.setupStoreSubscription();
    }

    public getLayoutHeight() { return this.layoutHeight; }
    public onLayoutChange?: () => void;

    private loadAssets() {

        try {
            this.skillAtlasTexture = Assets.get(this.config.assets.atlasUrl);
            this.pipActiveTexture = Assets.get('pips_active');
            this.pipDisabledTexture = Assets.get('pips_disabled');
        } catch (e) {
            console.warn('SkillBarRenderer: Could not get assets', e);
        }
    }

    private setupStoreSubscription(): void {
        this.stateMachine.onStateChange((from, to) => {
            if (from === ControlState.PlayerMoving || from === ControlState.PlayerAttacking || from === ControlState.EnemyTurn || to === ControlState.EnemyTurn) {
                const { selectedUnitId, selectedSkillId } = useBattleStore.getState();
                this.render(selectedUnitId, selectedSkillId);
            }
        });
        this.unsubscribe = useBattleStore.subscribe(
            (state) => [
                state.selectedUnitId,
                state.selectedSkillId,
                state.selectedPath?.cost,
            ] as const,
            ([selectedUnitId, selectedSkillId]) => {
                this.render(selectedUnitId, selectedSkillId);
            },
            { equalityFn: shallow }
        );
    }

    private refreshRender() {
        const state = useBattleStore.getState();
        this.render(state.selectedUnitId, state.selectedSkillId);
    }

    private render(
        unitId: string | null,
        selectedSkillId: string | null
    ): void {
        if (!unitId || this.stateMachine.is(ControlState.EnemyTurn)) {
            this.clearIcons();
            return;
        }

        const unit = useBattleStore.getState().units.get(unitId);
        if (!unit) return;

        const pathCost = useBattleStore.getState().selectedPath?.cost ?? 0;
        const remainingStamina = Math.max(0, unit.currentStamina - pathCost);

        this.clearIcons();

        let offset = 0;
        const { gap, iconSize, direction } = this.config;

        const sortedSkills = [...unit.skills].sort((a: Skill, b: Skill) => a.sort - b.sort);
        sortedSkills.forEach((skill: Skill, index: number) => {
            const canSelect = unit.currentStamina >= skill.cost;
            const isSelected = skill.id === selectedSkillId;

            const maxPips = Math.floor(unit.stats.stamina / skill.cost);
            const activePips = Math.floor(remainingStamina / skill.cost);
            const hotkeyLabel = String(index + 1);
            const icon = this.createSkillIcon(skill, isSelected, canSelect, maxPips, activePips, this.handleSkillClick, hotkeyLabel);

            if (direction === 'vertical') {
                icon.container.position.set(iconSize + gap, offset);
            } else {
                icon.container.position.set(offset, 0);
            }

            this.addChild(icon.container);
            offset += iconSize + gap;
        });

        // offset += gap * 0.5;

        // const endTurnBtn = this.createEndTurnButton(unit.unitId);
        const endTurnBtn = this.createSkillIcon({iconCol: 2, iconRow: 0, id: unitId}, false, true, 0, 0, this.handleEndTurnClick, "F");
        this.addChild(endTurnBtn.container);
        // endTurnBtn.position.set(0, offset);

        if (direction === 'vertical') {
            endTurnBtn.container.position.set(-gap, offset - iconSize - gap);
            this.layoutHeight = offset;
        } else {
            const secondOffset = offset + iconSize + gap;
            endTurnBtn.container.position.set(secondOffset, 0);
            this.layoutHeight = iconSize;
        }

        this.onLayoutChange?.();
    }

    private createSkillIcon(
        { iconCol, iconRow, id }: { iconCol: number, iconRow: number, id: string }, 
        isSelected: boolean, 
        isEnabled: boolean, 
        maxPips: number, 
        activePips: number, 
        handleClick: (id: string) => void,
        hotkeyLabel: string
    ): SkillIcon {
        const container = new Container();
        const { iconSize, borderWidth, colors } = this.config;

        const background = new Graphics();
        background.roundRect(0, 0, iconSize, iconSize, 6);
        background.fill(colors.background);

        let content: Sprite | Graphics | BitmapText;

        if (this.skillAtlasTexture) {
            const atlasSize = this.config.assets.iconSizeInAtlas;
            const col = iconCol || 0;
            const row = iconRow || 0;

            const frame = new Rectangle(
                col * atlasSize,
                row * atlasSize,
                atlasSize,
                atlasSize
            );

            const texture = new Texture({
                source: this.skillAtlasTexture.source,
                frame: frame
            });

            const sprite = new Sprite(texture);
            sprite.width = iconSize - borderWidth * 2;
            sprite.height = iconSize - borderWidth * 2;
            sprite.position.set(borderWidth, borderWidth);

            content = sprite;

            if (!isEnabled && content instanceof Sprite) {
                content.tint = 0x777066;
                content.alpha = 0.6;
            }
        } else {
            const style = new TextStyle({
                fontFamily: 'Alegreya, serif',
                fontSize: 24,
                fontWeight: '700',
                fill: isEnabled ? colors.text : 0x888888,
            });
            content = new BitmapText({ text: "skill", style });
            content.anchor.set(0.5);
            content.position.set(iconSize / 2, iconSize / 2);
        }

        const border = new Graphics();
        const borderColor = isSelected
            ? colors.borderSelected
            : (isEnabled ? colors.borderDefault : colors.borderDisabled);

        border.roundRect(0, 0, iconSize, iconSize, 6);
        border.stroke({
            width: borderWidth,
            color: borderColor,
            alpha: isEnabled ? 1 : 0.6
        });
        border.stroke({ width: borderWidth, color: 0x8c6a2b, alpha: 0.9 });

        if (isSelected) {
            const shine = new Graphics();
            shine.roundRect(2, 2, iconSize - 4, iconSize - 4, 5);
            shine.stroke({ width: 2, color: colors.borderSelected, alpha: 0.9 });
            container.addChild(shine);
        }

        const iconContainer = new Container();
        iconContainer.addChild(background);
        iconContainer.addChild(content);
        iconContainer.addChild(border);

        const hotkeyText = new BitmapText({
            text: hotkeyLabel,
            style: { 
                fontFamily: 'KyivTypeBitmap', 
                fontSize: 14,
            },
            tint: isEnabled ? 0xc9bda8 : 0x888888,
        });
        
        hotkeyText.anchor.set(0, 0);
        hotkeyText.position.set(6, 2); 
        
        iconContainer.addChild(hotkeyText);

        iconContainer.pivot.set(iconSize / 2, iconSize / 2);
        iconContainer.position.set(iconSize / 2, iconSize / 2);
        container.addChild(iconContainer);

        // --- Pips ---
        if (maxPips > 0 && (this.pipActiveTexture || this.pipDisabledTexture)) {
            const pipsContainer = new Container();
            const totalPipsWidth = maxPips * PIP_SIZE + (maxPips - 1) * PIP_GAP;
            const startX = (iconSize - totalPipsWidth) / 2;

            for (let i = 0; i < maxPips; i++) {
                const texture = i < activePips ? this.pipActiveTexture : this.pipDisabledTexture;
                if (!texture) continue;
                const pip = new Sprite(texture);
                pip.width = PIP_SIZE;
                pip.height = PIP_SIZE;
                pip.x = startX + i * (PIP_SIZE + PIP_GAP);
                pip.y = 0;
                pipsContainer.addChild(pip);
            }

            pipsContainer.y = iconSize - 16;
            iconContainer.addChild(pipsContainer);
        }

        // --- Interactivity ---
        container.eventMode = 'static';

        if (isEnabled) {
            container.on('pointerdown', () => { handleClick(id); });
            container.on('pointerover', () => {
                if (!isSelected) iconContainer.scale.set(1.1);
            });
            container.on('pointerout', () => {
                iconContainer.scale.set(1.0);
            });
        }

        const skillIcon: SkillIcon = {
            container,
            background,
            sprite: content as any,
            border,
            skillId: id
        };

        this.icons.set(id, skillIcon);
        return skillIcon;
    }


    private handleSkillClick = (skillId: string): void => {
        if (useBattleStore.getState().selectedSkillId === skillId) {
            this.skillManager.deselectSkill();
        } else {
            this.skillManager.selectSkill(skillId);
        }
    }


    private handleEndTurnClick = (unitId: string): void => {
        if (!this.stateMachine.canHandleInput(useBattleStore.getState().status)) return;
        this.turnManager.endUnitTurn(unitId);
    }

    private clearIcons(): void {
        this.icons.forEach((icon) => {
            icon.container.destroy({ children: true });
        });
        this.icons.clear();
        this.removeChildren();
    }

    public destroy(options?: any): void {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
        this.clearIcons();
        super.destroy(options);
    }
}